// SMS 단체발송 3단계 위저드 (받는 사람 → 내용 작성 → 확인 후 발송).
// 발송은 /dashboard/sms/bulk 로 {body, recipients} 전송 → 백엔드가 수신자별 치환 후 msg_queue + chat_messages INSERT.
// 변수 치환/바이트 판정은 백엔드와 동일 로직(아래 헬퍼)으로 미리보기.
import React, { useEffect, useRef, useState } from 'react';
import styles from '../componentsStyle/smsbulksend.module.css';
import { getFetch, postFetch } from '../../fetch/fetch';
import { Button, CircularProgress, Modal, Box } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import SendIcon from '@mui/icons-material/Send';
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import CheckIcon from '@mui/icons-material/Check';
import PeopleAltOutlinedIcon from '@mui/icons-material/PeopleAltOutlined';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import CloseIcon from '@mui/icons-material/Close';

const CALLBACK_DISPLAY = "1844-0572";

//백엔드와 동일: ASCII 1byte / 그 외 2byte. 90byte 초과 → LMS
const smsByteLength = (s: string) => {
    let bytes = 0;
    for (const ch of ("" + (s || ""))) {
        const code = ch.codePointAt(0) || 0;
        bytes += (code <= 0x7F) ? 1 : 2;
    }
    return bytes;
};

const normalizePhone = (p: any) => ("" + (p || "")).replace(/[^0-9]/g, "");

const formatPhone = (p: any) => {
    const d = normalizePhone(p);
    if (d.length === 11) return `${d.slice(0, 3)}-${d.slice(3, 7)}-${d.slice(7)}`;
    if (d.length === 10) {
        if (d.startsWith("02")) return `${d.slice(0, 2)}-${d.slice(2, 6)}-${d.slice(6)}`;
        return `${d.slice(0, 3)}-${d.slice(3, 6)}-${d.slice(6)}`;
    }
    return ("" + (p || ""));
};

//백엔드 substitute와 동일
const substitute = (body: string, r: any) =>
    ("" + (body || ""))
        .split("{{이름}}").join(r.name || "")
        .split("{{설명}}").join(r.description || "")
        .split("{{전화번호}}").join(formatPhone(r.phone));

const isValidRow = (r: any) => !!(r && r.name && ("" + r.name).trim()) && !!normalizePhone(r.phone);

const formModalStyle: any = {
    position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
    width: 460, maxHeight: '70vh', bgcolor: '#fff', borderRadius: '12px', boxShadow: 24, outline: 'none',
    display: 'flex', flexDirection: 'column', overflow: 'hidden'
};

const nextSx: any = { bgcolor: "#303030", color: "#fff", "&:hover": { bgcolor: "#222" }, borderRadius: "8px", fontFamily: "Apple_R", boxShadow: "none", px: 3, py: 1 };
const prevSx: any = { borderColor: "#d6d6d6", color: "#555", "&:hover": { borderColor: "#bbb", bgcolor: "transparent" }, borderRadius: "8px", fontFamily: "Apple_R", px: 3, py: 1 };

const STEPS = [
    { key: 1, label: "받는 사람", Icon: PeopleAltOutlinedIcon },
    { key: 2, label: "내용 작성", Icon: ChatBubbleOutlineIcon },
    { key: 3, label: "확인 후 발송", Icon: SendIcon },
];

const SmsBulkSend: React.FC<any> = (props) => {

    const [step, setStep] = useState(1);
    const [recipients, setRecipients] = useState<any[]>([]);
    const [input, setInput] = useState<any>({ name: "", phone: "", description: "" });
    const [body, setBody] = useState("");
    const [sending, setSending] = useState(false);

    const [templates, setTemplates] = useState<any[]>([]);
    const [templateOpen, setTemplateOpen] = useState(false);

    const textareaRef = useRef<any>(null);

    const validRecipients = recipients.filter(isValidRow).map((r: any) => ({
        name: ("" + r.name).trim(),
        phone: normalizePhone(r.phone),
        description: ("" + (r.description || "")).trim(),
    }));

    //가장 긴 수신자 기준 바이트로 타입 판정
    let maxBytes = smsByteLength(body);
    validRecipients.forEach((r: any) => {
        const b = smsByteLength(substitute(body, r));
        if (b > maxBytes) maxBytes = b;
    });
    const msgType = maxBytes > 90 ? "LMS" : "SMS";

    const fetchTemplates = async () => {
        try {
            const result: any = await getFetch("/dashboard/sms/templates");
            if (result.message === "success") setTemplates(result.data);
        } catch (e) {
            //무시
        }
    };

    useEffect(() => {
        fetchTemplates();
    }, []);

    //입력행 추가 (이름/번호 빠져도 추가하되 경고 표시. 전부 비면 무시)
    const addRecipientFromInput = () => {
        const name = ("" + input.name).trim();
        const phone = normalizePhone(input.phone);
        const description = ("" + input.description).trim();
        if (!name && !phone && !description) return;
        setRecipients((prev: any[]) => [...prev, { name, phone, description }]);
        setInput({ name: "", phone: "", description: "" });
    };

    //엑셀(탭/줄바꿈) 붙여넣기 → 여러 행 일괄 추가. 단일 셀 붙여넣기는 기본 동작 유지.
    const handlePaste = (e: any) => {
        const data = (e.clipboardData && e.clipboardData.getData("text")) || "";
        if (data.indexOf("\t") === -1 && data.indexOf("\n") === -1 && data.indexOf("\r") === -1) return;
        e.preventDefault();
        const lines = data.split(/\r\n|\r|\n/).filter((l: string) => l.trim() !== "");
        const parsed = lines.map((line: string) => {
            const cols = line.split("\t");
            return {
                name: (cols[0] || "").trim(),
                phone: normalizePhone(cols[1] || ""),
                description: (cols[2] || "").trim(),
            };
        });
        if (parsed.length > 0) setRecipients((prev: any[]) => [...prev, ...parsed]);
    };

    const removeRecipient = (index: number) => {
        setRecipients((prev: any[]) => prev.filter((_: any, i: number) => i !== index));
    };

    const insertVariable = (token: string) => {
        const ta = textareaRef.current;
        if (!ta) { setBody((b: string) => b + token); return; }
        const start = ta.selectionStart != null ? ta.selectionStart : body.length;
        const end = ta.selectionEnd != null ? ta.selectionEnd : body.length;
        const next = body.slice(0, start) + token + body.slice(end);
        setBody(next);
        setTimeout(() => {
            ta.focus();
            const pos = start + token.length;
            ta.selectionStart = pos;
            ta.selectionEnd = pos;
        }, 0);
    };

    const saveTemplate = async () => {
        const name = window.prompt("템플릿 이름을 입력하세요 (같은 이름은 덮어쓰기)");
        if (name == null) return;
        const trimmed = name.trim();
        if (!trimmed) return;
        try {
            const result: any = await postFetch("/dashboard/sms/templates/save", { name: trimmed, body });
            if (result.message === "success") fetchTemplates();
        } catch (e) {
            //무시
        }
    };

    const loadTemplate = (t: any) => {
        setBody(t.body || "");
        setTemplateOpen(false);
    };

    const deleteTemplate = async (id: number) => {
        try {
            const result: any = await postFetch("/dashboard/sms/templates/delete", { id });
            if (result.message === "success") fetchTemplates();
        } catch (e) {
            //무시
        }
    };

    const handleSend = async () => {
        if (sending) return;
        if (validRecipients.length === 0 || !body.trim()) return;
        setSending(true);
        try {
            const result: any = await postFetch("/dashboard/sms/bulk", { body, recipients: validRecipients });
            if (result.message === "success") {
                window.alert(`${result.data.count}명에게 발송했습니다. (SMS ${result.data.sms} / LMS ${result.data.lms})`);
                setRecipients([]);
                setBody("");
                setStep(1);
            } else {
                window.alert("발송 실패: " + result.message);
            }
        } catch (e) {
            window.alert("발송 중 오류가 발생했습니다.");
        } finally {
            setSending(false);
        }
    };

    const previewRecipient = validRecipients[0];

    return (
        <div className={styles.wrap}>

            {/* 헤더 */}
            <div className={styles.header}>
                <div>
                    <div className={styles.eyebrow}>수능선배 · 메시지</div>
                    <div className={styles.bigTitle}>단체발송</div>
                </div>
                <div className={styles.callbackBox}>
                    <div className={styles.callbackLabel}>발신번호</div>
                    <div className={styles.callbackNum}>{CALLBACK_DISPLAY}</div>
                </div>
            </div>

            {/* 단계 인디케이터 */}
            <div className={styles.steps}>
                {STEPS.map((s, idx) => {
                    const done = step > s.key;
                    const active = step === s.key;
                    const Icon = s.Icon;
                    return (
                        <React.Fragment key={s.key}>
                            <div className={styles.stepItem}>
                                <div className={`${styles.stepCircle} ${done ? styles.stepDone : active ? styles.stepActive : ""}`}>
                                    {done ? <CheckIcon sx={{ fontSize: 18 }} /> : <Icon sx={{ fontSize: 18 }} />}
                                </div>
                                <span className={`${styles.stepLabel} ${(done || active) ? styles.stepLabelOn : ""}`}>{s.label}</span>
                            </div>
                            {idx < STEPS.length - 1 && <div className={`${styles.stepLine} ${step > s.key ? styles.stepLineOn : ""}`} />}
                        </React.Fragment>
                    );
                })}
            </div>

            {/* ===== 1단계: 받는 사람 ===== */}
            {step === 1 && (
                <>
                    <div className={styles.card}>
                        <div className={styles.cardHead}>
                            <span className={styles.cardTitle}>받는 사람</span>
                            <span className={styles.countPill}>{validRecipients.length}명</span>
                        </div>
                        <p className={styles.desc}>이름과 전화번호는 필수, 설명은 메모용입니다. 입력한 이름·설명·전화번호는 다음 단계에서 변수로 끼워 넣을 수 있어요.</p>

                        <div className={styles.inputRow}>
                            <input className={styles.input} placeholder="이름" value={input.name}
                                onChange={(e) => setInput({ ...input, name: e.target.value })} onPaste={handlePaste}
                                onKeyDown={(e) => { if (e.key === "Enter") addRecipientFromInput(); }} />
                            <input className={styles.input} placeholder="전화번호" value={input.phone}
                                onChange={(e) => setInput({ ...input, phone: e.target.value })} onPaste={handlePaste}
                                onKeyDown={(e) => { if (e.key === "Enter") addRecipientFromInput(); }} />
                            <input className={styles.input} placeholder="설명 (선택)" value={input.description}
                                onChange={(e) => setInput({ ...input, description: e.target.value })} onPaste={handlePaste}
                                onKeyDown={(e) => { if (e.key === "Enter") addRecipientFromInput(); }} />
                            <div className={styles.addBtn} onClick={addRecipientFromInput}><AddIcon /></div>
                        </div>

                        {recipients.length > 0 && (
                            <div className={styles.table}>
                                <div className={`${styles.tr} ${styles.thead}`}>
                                    <span className={styles.cName}>이름</span>
                                    <span className={styles.cPhone}>전화번호</span>
                                    <span className={styles.cDesc}>설명</span>
                                    <span className={styles.cDel}></span>
                                </div>
                                {recipients.map((r: any, i: number) => {
                                    const invalid = !isValidRow(r);
                                    return (
                                        <div key={i} className={`${styles.tr} ${invalid ? styles.trInvalid : ""}`}>
                                            <span className={styles.cName}>{r.name || <span className={styles.warnText}>이름 없음</span>}</span>
                                            <span className={styles.cPhone}>{r.phone ? formatPhone(r.phone) : <span className={styles.warnText}>번호 없음</span>}</span>
                                            <span className={styles.cDesc}>{r.description || "—"}</span>
                                            <span className={styles.cDel}><DeleteOutlineIcon sx={{ fontSize: 18, cursor: "pointer", color: "#b8b8b8" }} onClick={() => removeRecipient(i)} /></span>
                                        </div>
                                    );
                                })}
                            </div>
                        )}

                        <div className={styles.hint}>📋 엑셀에서 이름·전화번호·설명 열을 복사해 붙여넣으면 한꺼번에 추가됩니다.</div>
                        {recipients.some((r: any) => !isValidRow(r)) && (
                            <div className={styles.warnBanner}>이름 또는 번호가 빠진 행은 발송 대상에서 제외됩니다.</div>
                        )}
                    </div>
                    <div className={styles.footer}>
                        <span />
                        <Button variant="contained" sx={nextSx} disabled={validRecipients.length === 0} onClick={() => setStep(2)}>다음 ›</Button>
                    </div>
                </>
            )}

            {/* ===== 2단계: 내용 작성 ===== */}
            {step === 2 && (
                <>
                    <div className={styles.card}>
                        <div className={styles.cardHead}>
                            <span className={styles.cardTitle}>내용 작성</span>
                            <div className={styles.headActions}>
                                <div className={styles.ghostBtn} onClick={() => { fetchTemplates(); setTemplateOpen(true); }}><MailOutlineIcon sx={{ fontSize: 16, marginRight: "4px" }} /> 내 문자함</div>
                                <div className={styles.ghostBtn} onClick={saveTemplate}><SaveOutlinedIcon sx={{ fontSize: 16, marginRight: "4px" }} /> 저장하기</div>
                                <span className={`${styles.typeBadge} ${msgType === "LMS" ? styles.typeLms : ""}`}>{msgType} · 최대 {maxBytes}바이트</span>
                            </div>
                        </div>
                        <p className={styles.desc}>변수를 누르면 커서 위치에 들어갑니다. 발송할 때 받는 사람마다 실제 값으로 바뀝니다.</p>

                        <div className={styles.chips}>
                            <span className={styles.chip} onClick={() => insertVariable("{{이름}}")}>{"{{이름}}"}</span>
                            <span className={styles.chip} onClick={() => insertVariable("{{설명}}")}>{"{{설명}}"}</span>
                            <span className={styles.chip} onClick={() => insertVariable("{{전화번호}}")}>{"{{전화번호}}"}</span>
                        </div>

                        <textarea ref={textareaRef} className={styles.textarea} value={body} onChange={(e) => setBody(e.target.value)}
                            placeholder="{{이름}} 고객님, 안녕하세요. 수능선배입니다." />

                        <div className={styles.byteRow}>
                            <span className={styles.byteHint}>90바이트 이하면 SMS, 넘으면 자동으로 LMS로 발송돼요.</span>
                            <span className={`${styles.byteMax} ${msgType === "LMS" ? styles.byteMaxLms : ""}`}>가장 긴 메시지 {maxBytes}바이트 → {msgType}</span>
                        </div>

                        {previewRecipient && (
                            <div className={styles.previewBlock}>
                                <div className={styles.previewLabel}>👁 {previewRecipient.name} 님 기준 미리보기</div>
                                <div className={styles.previewBubble}>{substitute(body, previewRecipient)}</div>
                            </div>
                        )}
                    </div>
                    <div className={styles.footer}>
                        <Button variant="outlined" sx={prevSx} onClick={() => setStep(1)}>‹ 이전</Button>
                        <Button variant="contained" sx={nextSx} disabled={!body.trim()} onClick={() => setStep(3)}>다음 ›</Button>
                    </div>
                </>
            )}

            {/* ===== 3단계: 확인 후 발송 ===== */}
            {step === 3 && (
                <>
                    <div className={styles.card}>
                        <div className={styles.cardHead}>
                            <span className={styles.cardTitle}>확인 후 발송</span>
                            <span className={styles.countPill}>{validRecipients.length}명</span>
                        </div>

                        <div className={styles.summaryGrid}>
                            <div className={styles.summaryCell}><div className={styles.sumLabel}>받는 사람</div><div className={styles.sumValue}>{validRecipients.length}명</div></div>
                            <div className={styles.summaryCell}><div className={styles.sumLabel}>발송 유형</div><div className={styles.sumValue}>{msgType}</div></div>
                            <div className={styles.summaryCell}><div className={styles.sumLabel}>발신번호</div><div className={styles.sumValue}>{CALLBACK_DISPLAY}</div></div>
                            <div className={styles.summaryCell}><div className={styles.sumLabel}>발송 시점</div><div className={styles.sumValue}>지금 바로</div></div>
                        </div>

                        <p className={styles.desc}>받는 사람마다 변수가 실제로 어떻게 바뀌는지 확인하세요. 보이는 그대로 발송됩니다.</p>

                        <div className={styles.previewList}>
                            {validRecipients.map((r: any, i: number) => (
                                <div key={i} className={styles.previewCard}>
                                    <div className={styles.previewCardHead}>
                                        <span className={styles.previewName}>{r.name}</span>
                                        <span className={styles.previewPhone}>{formatPhone(r.phone)}</span>
                                    </div>
                                    <div className={styles.previewBubble}>{substitute(body, r)}</div>
                                </div>
                            ))}
                        </div>

                        <div className={styles.sendBtn} onClick={handleSend}>
                            {sending ? <CircularProgress size={20} sx={{ color: "#fff" }} /> : <><SendIcon sx={{ fontSize: 18, marginRight: "8px" }} /> {validRecipients.length}명에게 발송</>}
                        </div>
                        <div className={styles.sendWarn}>한 번 발송하면 취소할 수 없어요. 위 미리보기를 꼭 확인하세요.</div>
                    </div>
                    <div className={styles.footer}>
                        <Button variant="outlined" sx={prevSx} onClick={() => setStep(2)}>‹ 이전</Button>
                        <span />
                    </div>
                </>
            )}

            {/* 내 문자함 모달 */}
            <Modal open={templateOpen} onClose={() => setTemplateOpen(false)}>
                <Box sx={formModalStyle}>
                    <div className={styles.modalHeader}>
                        <span className={styles.modalTitle}>내 문자함</span>
                        <CloseIcon sx={{ cursor: "pointer", color: "#888" }} onClick={() => setTemplateOpen(false)} />
                    </div>
                    <div className={styles.templateList}>
                        {templates.length === 0 ? (
                            <div className={styles.templateEmpty}>저장된 템플릿이 없습니다.</div>
                        ) : templates.map((t: any) => (
                            <div key={t.id} className={styles.templateRow}>
                                <div className={styles.templateInfo} onClick={() => loadTemplate(t)}>
                                    <div className={styles.templateName}>{t.name}</div>
                                    <div className={styles.templatePreview}>{(t.body || "").replace(/\n/g, " ").slice(0, 40)}</div>
                                </div>
                                <span className={styles.templateLoad} onClick={() => loadTemplate(t)}>불러오기</span>
                                <span className={styles.templateDelete} onClick={() => deleteTemplate(t.id)}>삭제</span>
                            </div>
                        ))}
                    </div>
                </Box>
            </Modal>

        </div>
    );
};

export default SmsBulkSend;
